import * as React from 'react';
import { Button, Form, Input, Modal, Select } from 'antd';
import { Formik, FormikProps } from "formik";
import { useEffect, useRef } from 'react';
import * as Yup from 'yup';
import { DeletionApprovalStatusType } from '../../../constants/deletionApprovalStatus';
import { CompanyDeletionRequest } from '../../../models/company-deletion-interface';

const { Option } = Select;

type Props = {
  isVisible: boolean,
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>,
  handleSubmit: Function,
  isLoading: boolean,
  selectedCompanies: CompanyDeletionRequest[]
}

const RespondDeletionRequestModal: React.FC<Props> = ({
  isVisible,
  setIsVisible,
  handleSubmit,
  isLoading,
  selectedCompanies
}) => {
  const formRef = useRef<FormikProps<any>>(null);

  useEffect(() => {
    if (!isVisible) {
      formRef.current?.resetForm();
    }
  }, [isVisible]);

  return (
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={{
          approvalTypeId: null,
          approvalConfirmString: ""
        }}
        onSubmit={(val) => handleSubmit(val)}
        validationSchema={
          Yup.object().shape({
            approvalTypeId: Yup.number().required().typeError("Please select an approval status"),
            approvalConfirmString: Yup.string()
          })
        }
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleSubmit,
          setFieldValue
        }) => {
          var hasApproveString = values.approvalTypeId !== DeletionApprovalStatusType.Approved || values.approvalConfirmString === "Approve";
          return (
            <Modal
            centered
            title="Respond to Deletion Request"
            visible={isVisible}
            onCancel={() => setIsVisible(false)}
            footer={[
              <Button id="cancel" type="text" key="cancel" onClick={() => setIsVisible(false)}>
                Cancel
              </Button>,
              <Button id={`submitButton`} type="primary" key="submit" htmlType="submit" form="respondDeleteForm" loading={isLoading} disabled={!(values.approvalTypeId > 0 && hasApproveString)}>
                Submit
              </Button>
            ]}
            destroyOnClose
          >
            <p>
              Selected Companies: <br />
              {selectedCompanies?.map((company) => (
                company.CompanyName
              )).join(", ")}
            </p>
            <p>Do you want to approve or reject {selectedCompanies?.length > 1 ? "these" : "this"} deletion request{selectedCompanies?.length > 1 ? "s" : ""}?</p>
            <Form
              id="respondDeleteForm"
              onFinish={handleSubmit}
              layout="vertical"
            >
              <Form.Item
                label="Approval Status"
                help={(touched.approvalTypeId && errors.approvalTypeId ? errors.approvalTypeId : null)}
                validateStatus={touched.approvalTypeId && errors.approvalTypeId ? 'error' : ''}
                required
              >
                <Select
                  value={values.approvalTypeId}
                  onChange={(value) => setFieldValue('approvalTypeId', value)}
                  onBlur={() => handleBlur('approvalTypeId')}
                  style={{ width: '100%' }}
                  placeholder="Select an approval status"
                  showSearch
                  filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                >
                  {Object.keys(DeletionApprovalStatusType).filter(key => !isNaN(Number(DeletionApprovalStatusType[key])) && DeletionApprovalStatusType[key] < DeletionApprovalStatusType.InProgress).map((val) => (
                    <Option key={DeletionApprovalStatusType[val]} value={DeletionApprovalStatusType[val]} label={val}>
                      {val}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {values.approvalTypeId === DeletionApprovalStatusType.Approved && (
                <>
                  <p style={{ marginTop: 15 }}>Are you sure you want to approve this deletion request? This will delete the company and all associated data permanently and cannot be undone. Please type "Approve" to confirm.</p>
                  <Input value={values.approvalConfirmString} onChange={(e) => setFieldValue('approvalConfirmString', e.target.value)} onBlur={() => handleBlur('approvalConfirmString')} />
                </>
              )}
            </Form>
          </Modal>)
        }}
      </Formik>
  )
};

export default RespondDeletionRequestModal;